import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Assessment,
  Close,
  Delete,
  Drafts,
  Edit,
  EditNoteOutlined,
  Event,
  LockOutlined,
  Publish,
  School,
  Subject as SubjectIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { markedExamBlock, progressText } from './examGroupGuards';
import { fmtClassName } from '../../utils/pdf';

import { ExamGroupDetail } from '../../types';
import { examService } from '../../services/examService';

interface ExamDetailDrawerProps {
  open: boolean;
  examGroupId: string | null;
  canManage: boolean;
  onClose: () => void;
  onEdit: (group: ExamGroupDetail) => void;
  onPublish: (group: ExamGroupDetail) => void;
  onUnpublish: (group: ExamGroupDetail) => void;
  onDelete: (group: ExamGroupDetail) => void;
}

const ExamDetailDrawer: React.FC<ExamDetailDrawerProps> = ({
  open,
  examGroupId,
  canManage,
  onClose,
  onEdit,
  onPublish,
  onUnpublish,
  onDelete,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [group, setGroup] = useState<ExamGroupDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!examGroupId) return;
    setLoading(true);
    try {
      const response = await examService.getExamGroup(examGroupId);
      setGroup(response.data ?? null);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load the exam', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [examGroupId, enqueueSnackbar]);

  useEffect(() => {
    if (open && examGroupId) {
      load();
    } else if (!open) {
      // Clear stale state so the next open shows a clean skeleton.
      setGroup(null);
    }
  }, [open, examGroupId, load]);

  const totalSubjects = group?.perClass.reduce((sum, p) => sum + p.subjects.length, 0) ?? 0;
  const isDraft = group?.status === 'draft';
  const unpublishBlock = markedExamBlock(group?.progress, 'unpublish');
  const deleteBlock = markedExamBlock(group?.progress, 'delete');

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      slotProps={{
        root: { style: { zIndex: 1250 } },
        paper: {
          style: { zIndex: 1250 },
          sx: {
            width: { xs: '100%', sm: 540 },
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 1.5 }}
        >
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
            Exam
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        {loading || !group ? (
          <Stack spacing={1}>
            <Skeleton variant="text" width={220} height={32} />
            <Skeleton variant="text" width={160} height={20} />
          </Stack>
        ) : (
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.18),
                color: theme.palette.primary.dark,
              }}
            >
              <Assessment />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.1, mb: 0.5 }}>
                {group.name}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                <Chip
                  label={group.status === 'published' ? 'Published' : 'Draft'}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    ...(group.status === 'published'
                      ? {
                          bgcolor: alpha(theme.palette.success.main, 0.12),
                          color: 'success.main',
                        }
                      : {
                          bgcolor: alpha(theme.palette.warning.main, 0.12),
                          color: 'warning.main',
                          border: '1px solid',
                          borderColor: 'warning.main',
                        }),
                  }}
                />
                {group.examType?.name && (
                  <Chip
                    label={group.examType.name}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      bgcolor: alpha(theme.palette.secondary.main, 0.12),
                      color: 'secondary.main',
                    }}
                  />
                )}
                {group.session?.name && (
                  <Chip
                    label={group.session.name}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      bgcolor: alpha(theme.palette.info.main, 0.12),
                      color: 'info.main',
                    }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
        {loading || !group ? (
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={140} />
            <Skeleton variant="rounded" height={140} />
          </Stack>
        ) : (
          <Stack spacing={2}>
            {/* Summary */}
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 0.6, mb: 1, display: 'block' }}
              >
                Overview
              </Typography>
              <Stack spacing={1}>
                <SummaryRow
                  icon={<Event fontSize="small" />}
                  label="Date Range"
                  value={
                    group.startDate && group.endDate
                      ? `${format(new Date(group.startDate), 'MMM dd, yyyy')} — ${format(
                          new Date(group.endDate),
                          'MMM dd, yyyy'
                        )}`
                      : '—'
                  }
                />
                <SummaryRow
                  icon={<School fontSize="small" />}
                  label="Classes"
                  value={`${group.perClass.length}`}
                />
                <SummaryRow
                  icon={<SubjectIcon fontSize="small" />}
                  label="Total Subjects"
                  value={`${totalSubjects} across ${group.perClass.length} class${
                    group.perClass.length === 1 ? '' : 'es'
                  }`}
                />
                <SummaryRow
                  icon={<Assessment fontSize="small" />}
                  label="Reports"
                  value={group.countsInReports ? 'Counts in transcripts, mark sheets and averages' : 'Not counted in reports'}
                />
              </Stack>
              {!isDraft && (
                <Box sx={{ mt: 2 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {group.progress.students} student{group.progress.students === 1 ? '' : 's'} · {progressText(group.progress)}
                    </Typography>
                    {group.progress.locked > 0 && <Typography variant="caption" color="text.secondary">{group.progress.locked} submitted</Typography>}
                  </Stack>
                  <LinearProgress variant="determinate" value={group.progress.papers ? (group.progress.recorded / group.progress.papers) * 100 : 0} sx={{ height: 6, borderRadius: 3 }} />
                </Box>
              )}
            </Paper>

            {/* Per-class breakdown */}
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 0.6, mb: 1, display: 'block', px: 0.5 }}
              >
                Per-class subjects
              </Typography>
              <Stack spacing={1.25}>
                {group.perClass.map(entry => {
                  const total = entry.subjects.reduce((s, x) => s + x.maxMarks, 0);
                  return (
                    <Paper
                      key={entry.examId}
                      variant="outlined"
                      sx={{ borderRadius: 2, overflow: 'hidden' }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                            color: 'primary.main',
                          }}
                        >
                          <School fontSize="small" />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {entry.class ? fmtClassName(entry.class.name) : '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.subjects.length} subject
                            {entry.subjects.length === 1 ? '' : 's'} · Total {total} marks
                            {entry.status === 'published' && ` · ${entry.progress.students} student${entry.progress.students === 1 ? '' : 's'}`}
                          </Typography>
                        </Box>
                        <Chip
                          label={entry.status === 'published' ? 'Published' : 'Draft'}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            ...(entry.status === 'published'
                              ? {
                                  bgcolor: alpha(theme.palette.success.main, 0.12),
                                  color: 'success.main',
                                }
                              : {
                                  bgcolor: alpha(theme.palette.warning.main, 0.12),
                                  color: 'warning.main',
                                }),
                          }}
                        />
                      </Box>
                      {entry.subjects.length === 0 ? (
                        <Box sx={{ px: 2, py: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            No subjects assigned.
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          {entry.subjects.map((s, idx) => (
                            <Box
                              key={s.subjectId._id}
                              sx={{
                                px: 2,
                                py: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                borderTop: idx === 0 ? 'none' : '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Box
                                sx={{
                                  width: 4,
                                  height: 22,
                                  borderRadius: 1,
                                  bgcolor: theme.palette.primary.main,
                                  opacity: 0.5,
                                }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {s.subjectId?.name ?? '—'}
                                </Typography>
                                {entry.status === 'published' && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {s.papers > 0 && s.locked === s.papers && <LockOutlined sx={{ fontSize: 13 }} />}
                                    {s.papers === 0 ? 'Nobody sits it' : s.locked === s.papers ? 'Submitted' : `${s.recorded} of ${s.papers} marked`}
                                  </Typography>
                                )}
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontVariantNumeric: 'tabular-nums',
                                  fontWeight: 600,
                                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                                  color: 'primary.main',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                }}
                              >
                                {s.maxMarks} marks
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        )}
      </Box>

      {/* Footer actions */}
      {canManage && group && !loading && (
        <>
          <Divider />
          <Box
            sx={{
              p: 2,
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              bgcolor: 'background.paper',
            }}
          >
            <Button
              variant="outlined"
              startIcon={<Edit fontSize="small" />}
              onClick={() => onEdit(group)}
              sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Edit
            </Button>
            {isDraft ? (
              <Button
                variant="contained"
                color="success"
                startIcon={<Publish fontSize="small" />}
                onClick={() => onPublish(group)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Publish
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<EditNoteOutlined fontSize="small" />}
                  onClick={() => navigate(`/app/examination/marks?examGroupId=${group.examGroupId}`)}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Enter marks
                </Button>
                <Tooltip title={unpublishBlock || 'Back to draft'} arrow>
                  <span>
                    <IconButton
                      color="warning"
                      onClick={() => onUnpublish(group)}
                      disabled={!!unpublishBlock}
                      aria-label="Unpublish"
                      sx={{ border: '1px solid', borderColor: alpha(theme.palette.warning.main, 0.4), borderRadius: 2 }}
                    >
                      <Drafts fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            <Tooltip title={deleteBlock || 'Delete exam'} arrow>
              <span>
                <IconButton
                  color="error"
                  onClick={() => onDelete(group)}
                  disabled={!!deleteBlock}
                  aria-label="Delete exam"
                  sx={{
                    border: '1px solid',
                    borderColor: alpha(theme.palette.error.main, 0.4),
                    borderRadius: 2,
                    '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </>
      )}
    </Drawer>
  );
};

interface SummaryRowProps {
  icon: React.ReactElement;
  label: string;
  value: string;
}

const SummaryRow: React.FC<SummaryRowProps> = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.25 }}>
    <Box sx={{ color: 'text.disabled', display: 'flex' }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  </Stack>
);

export default ExamDetailDrawer;
